import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Shield,
  Lock,
  User,
  Terminal,
  ArrowRight,
  CheckCircle2,
  KeyRound,
  Radio,
  Cpu,
} from "lucide-react";
import "./login.css";
import { ROLE_DETAILS as ROLES } from "../roles";

const Login = () => {
  const [form, setForm] = useState({ username: "", password: "", role: "controller" });
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      axios
        .post("https://license-worker.alphatrendz99.workers.dev/api/verify", { token })
        .then((res) => {
          if (res.data.valid) {
            setTokenValid(true);
          } else {
            setTokenValid(false);
            localStorage.removeItem("token");
            localStorage.removeItem("user");
          }
        })
        .catch(() => {
          setTokenValid(false);
          localStorage.removeItem("token");
          localStorage.removeItem("user");
        });
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!form.role) {
      setErrorMsg("Please select an authorization role.");
      return;
    }
    setErrorMsg("");
    setLoading(true);
    localStorage.removeItem("token");
    localStorage.removeItem("user");

    try {
      const res = await axios.post(
        "https://license-worker.alphatrendz99.workers.dev/api/login",
        form
      );
      const { username, role, token } = res.data;

      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify({ username, role }));
      setLoading(false);
      navigate("/dashboard");
    } catch (err) {
      setLoading(false);
      setErrorMsg(err.response?.data?.message || "Invalid credentials or authorization failed.");
    }
  };

  const goToDashboard = () => {
    navigate("/dashboard");
  };

  return (
    <div className="bf-auth-container">
      {/* Brand HUD Header */}
      <div className="bf-auth-header">
        <div className="bf-brand-mark">
          <Terminal size={22} strokeWidth={2} />
        </div>
        <h1 className="bf-auth-title">BLACKFYRE</h1>
        <p className="bf-auth-subtitle">OPERATOR ACCESS CONSOLE // V2.4</p>
      </div>

      {/* Terminal Auth Card */}
      <div className="bf-auth-card">
        <div className="bf-card-topbar">
          <span className="tech-label">
            <Radio size={12} className="status-dot green pulse" style={{ display: "inline" }} />
            AUTH_SESSION: IDLE
          </span>
          <span className="tech-badge green">SYSTEM SECURE</span>
        </div>

        {errorMsg && (
          <div
            style={{
              background: "var(--red-bg)",
              border: "1px solid var(--red-border)",
              color: "var(--red)",
              padding: "10px 14px",
              borderRadius: "var(--radius-md)",
              fontSize: "12.5px",
              fontFamily: "var(--font-mono)",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Lock size={14} />
            <span>{errorMsg}</span>
          </div>
        )}

        <form onSubmit={handleLogin} style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
          {/* Operator ID */}
          <div className="bf-field-group">
            <label className="tech-label" htmlFor="username">
              <User size={12} />
              OPERATOR ID
            </label>
            <div className="bf-input-wrapper">
              <input
                id="username"
                className="bf-input"
                name="username"
                placeholder="Enter operator username"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                required
                autoComplete="username"
              />
              <span className="bf-input-icon">
                <User size={16} />
              </span>
            </div>
          </div>

          {/* Password */}
          <div className="bf-field-group">
            <label className="tech-label" htmlFor="password">
              <KeyRound size={12} />
              SECURITY KEY / PASSWORD
            </label>
            <div className="bf-input-wrapper">
              <input
                id="password"
                className="bf-input"
                name="password"
                type="password"
                placeholder="••••••••••••"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                autoComplete="current-password"
              />
              <span className="bf-input-icon">
                <Lock size={16} />
              </span>
            </div>
          </div>

          {/* Role Clearance Selection */}
          <div className="bf-field-group">
            <label className="tech-label">
              <Shield size={12} />
              AUTHORIZATION CLEARANCE
            </label>
            <div className="bf-role-grid">
              {ROLES.map((r) => {
                const isSelected = form.role === r.id;
                return (
                  <div
                    key={r.id}
                    className={`bf-role-card ${isSelected ? "active" : ""}`}
                    onClick={() => setForm({ ...form, role: r.id })}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        setForm({ ...form, role: r.id });
                      }
                    }}
                  >
                    <div className="bf-role-info">
                      <span className="bf-role-name">{r.label}</span>
                      <span className="bf-role-tier">{r.tier}</span>
                    </div>
                    <span className={`status-dot ${isSelected ? "active" : ""}`} />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Primary Submit Button */}
          <button type="submit" className="bf-btn-primary" disabled={loading}>
            {loading ? (
              <>
                <Cpu size={16} className="status-dot green pulse" />
                AUTHENTICATING...
              </>
            ) : (
              <>
                INITIALIZE ACCESS
                <ArrowRight size={16} />
              </>
            )}
          </button>
        </form>

        {/* Existing Active Session Shortcut */}
        {tokenValid && (
          <div style={{ marginTop: "4px", paddingTop: "14px", borderTop: "1px solid var(--border-0)" }}>
            <button
              type="button"
              className="bf-btn-secondary"
              onClick={goToDashboard}
            >
              <CheckCircle2 size={15} color="var(--green-0)" />
              RESUME ACTIVE SESSION →
            </button>
          </div>
        )}
      </div>

      {/* Footer Info HUD */}
      <div className="bf-auth-footer">
        <div className="bf-auth-footer-item">
          <span className="status-dot green pulse" />
          <span>CF-WORKERS // LIVE</span>
        </div>
        <span>•</span>
        <div className="bf-auth-footer-item">
          <span>AES-256 HMAC</span>
        </div>
        <span>•</span>
        <div className="bf-auth-footer-item">
          <span>REGION: ASIA-SOUTH1</span>
        </div>
      </div>
    </div>
  );
};

export default Login;
